import { createResource, createSignal, For, Show } from "solid-js";
import type {
  AccessGrant,
  GrantRole,
  TenantUser,
} from "@energyplatform/client";
import { api, session } from "../services.ts";
import { locale, t } from "../i18n.ts";
import { formatAgo, formatDate, formatDateTime } from "../format.ts";
import { pathTo, siteTree } from "../context.ts";
import { treeRows } from "../tree.ts";
import { access, can } from "../access.ts";
import { Icon } from "../icons.tsx";
import { ErrorBox } from "../components/ErrorBox.tsx";
import {
  ConfirmDialog,
  Dialog,
  PageHeader,
  Skeleton,
  Tabs,
  useAction,
} from "../components/ui.tsx";

const ROLES = ["admin", "operator", "viewer"] as const;
const AREAS = [
  "view",
  "events",
  "reports",
  "configure",
  "finance",
  "control",
  "access",
] as const;
// What each platform role may do in each area: 2 full, 1 partial, 0 none. Mirrors the API's policies.
const MATRIX: Record<
  (typeof ROLES)[number],
  Record<(typeof AREAS)[number], number>
> = {
  admin: {
    view: 2,
    events: 2,
    reports: 2,
    configure: 2,
    finance: 2,
    control: 0,
    access: 2,
  },
  operator: {
    view: 2,
    events: 2,
    reports: 2,
    configure: 2,
    finance: 2,
    control: 0,
    access: 0,
  },
  viewer: {
    view: 2,
    events: 1,
    reports: 1,
    configure: 0,
    finance: 1,
    control: 0,
    access: 0,
  },
};
const TABS = ["people", "grants", "history", "roles"] as const;
/** Areas of grants on parts of the tree, in the order of the prototype's role matrix. */
const CAPABILITIES = [
  "measurements",
  "costs",
  "structure",
  "control",
  "automations",
  "integrations",
  "users",
  "documentation",
] as const;

/**
 * Users and access (prototype "Użytkownicy i dostępy"): the people who signed in to the organisation, the roles they
 * had then (from ZITADEL), and what they take part in — event rules, reports, tasks. Roles apply to the whole
 * organisation; access per branch of the tree, invitations and time-limited access are prepared but not active.
 */
export function UsersPage() {
  // People of the whole organisation are listed to those with a tenant-wide role; others manage their branches' grants.
  const tabs = () =>
    TABS.filter((x) => x !== "people" || access().unrestricted);
  const [tab, setTab] = createSignal<(typeof TABS)[number]>(tabs()[0]!);
  const [roles] = createResource(() => api.access.roles());
  const [grants, { refetch }] = createResource(() => api.access.grants());
  const [dialog, setDialog] = createSignal<GrantTarget>();
  // What happened in the identity provider for the last invitation: account made, role given, or to do by hand.
  const [directory, setDirectory] = createSignal<string>();
  return (
    <>
      <PageHeader
        eyebrow={t("users.eyebrow")}
        title={t("users.title")}
        sub={t("users.intro")}
      >
        <Show when={canGrant()}>
          <button type="button" class="btn" onClick={() => setDialog({})}>
            <Icon name="plus" />
            {t("users.invite")}
          </button>
        </Show>
      </PageHeader>
      <Show when={directory()}>
        {(d) => (
          <div class="note" style={{ "margin-bottom": "12px" }}>
            <Icon
              name={
                d() === "failed" || d() === "notConfigured" ? "alert" : "check"
              }
            />
            <span>{t(`users.directory.${d()}`)}</span>
            <button
              type="button"
              class="btn ghost sm"
              style={{ "margin-left": "auto" }}
              onClick={() => setDirectory(undefined)}
            >
              <Icon name="x" />
            </button>
          </div>
        )}
      </Show>
      <Tabs
        tabs={tabs()}
        value={tab()}
        onChange={setTab}
        label={(x) => t(`users.tab.${x}`)}
      />
      <Show when={tab() === "people"}>
        <People grants={grants() ?? []} onGrant={setDialog} />
      </Show>
      <Show when={tab() === "grants"}>
        <Grants
          grants={grants() ?? []}
          loading={grants.loading}
          error={grants.error}
          onChanged={() => void refetch()}
        />
      </Show>
      <Show when={tab() === "history"}>
        <History />
      </Show>
      <Show when={tab() === "roles"}>
        <Roles grantRoles={roles() ?? []} />
      </Show>
      <Show when={dialog()}>
        {(d) => (
          <GrantDialog
            target={d()}
            roles={roles() ?? []}
            onClose={() => setDialog(undefined)}
            onSaved={(outcome) => {
              void refetch();
              setDirectory(outcome === "hasRole" ? undefined : outcome);
            }}
          />
        )}
      </Show>
    </>
  );
}

/** Who the grant dialog gives access to: a person who signed in, or (no user) an address to invite. */
type GrantTarget = { user?: TenantUser; temporary?: boolean };

/** Organisation administrators give access everywhere; people with access to users in a branch within it. */
const canGrant = () => {
  const s = access();
  return s.unrestricted ? isAdmin() : (s.capabilities["users"] ?? 0) >= 1;
};

const isAdmin = () => {
  const me = sessionUser();
  return me?.roles.includes("admin") ?? false;
};

const sessionUser = () => {
  const s = session();
  return s.status === "signedIn" ? s.user : undefined;
};

const nodeName = (id: string) =>
  pathTo(siteTree()?.nodes ?? [], id)
    .map((n) => n.name)
    .join(" / ") || id;

const STATE_CHIP: Record<string, string> = {
  active: "ok",
  invited: "acc",
  expired: "",
  revoked: "",
};

function People(props: {
  grants: AccessGrant[];
  onGrant: (target: GrantTarget) => void;
}) {
  const [users] = createResource(() => api.events.users());
  const [rules] = createResource(() => api.events.rules());
  const [reports] = createResource(() => api.reports.list());
  const [events] = createResource(() => api.events.list());
  const [selected, setSelected] = createSignal<string>();
  const current = () =>
    (users() ?? []).find((u) => u.id === selected()) ?? users()?.[0];
  const ruleCount = (u: TenantUser) =>
    (rules() ?? []).filter((r) => r.recipients.includes(u.id)).length;
  const reportsOf = (u: TenantUser) =>
    (reports() ?? []).filter((r) => r.recipients.includes(u.id));
  const tasksOf = (u: TenantUser) =>
    (events() ?? []).filter((e) => e.assignee === u.id && e.stage !== "closed");
  return (
    <Show when={!users.error} fallback={<ErrorBox error={users.error} />}>
      <Show
        when={users()}
        fallback={
          <section class="card">
            <Skeleton lines={5} />
          </section>
        }
      >
        {(list) => (
          <div class="grid g-2 g-2s">
            <section class="card">
              <div class="card-h">
                <h2>{t("users.people")}</h2>
                <span class="meta">
                  {t("users.count", { count: list().length })}
                </span>
              </div>
              <table class="tbl">
                <tbody>
                  <For each={list()}>
                    {(u) => (
                      <tr
                        class={`click${current()?.id === u.id ? " sel" : ""}`}
                        onClick={() => setSelected(u.id)}
                      >
                        <td>
                          <b style={{ "font-weight": 500 }}>{u.name}</b>
                          <div class="hint">{u.email ?? ""}</div>
                        </td>
                        <td>
                          <For each={u.roles ?? []}>
                            {(r) => (
                              <span
                                class="chip"
                                style={{ "margin-right": "4px" }}
                              >
                                {t(`role.${r}`)}
                              </span>
                            )}
                          </For>
                        </td>
                        <td class="hint">{formatAgo(u.lastSeen, locale())}</td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
              <div class="card-b hint">{t("users.listHint")}</div>
            </section>
            <Show when={current()}>
              {(u) => (
                <section class="card">
                  <div class="card-h">
                    <div>
                      <div class="eyebrow">{u().email ?? ""}</div>
                      <h2 style={{ "font-size": "16px" }}>{u().name}</h2>
                    </div>
                  </div>
                  <div class="card-b" style={{ display: "grid", gap: "12px" }}>
                    <dl class="kv" style={{ margin: 0 }}>
                      <dt>{t("users.roles")}</dt>
                      <dd>
                        {(u().roles ?? []).length > 0
                          ? (u().roles ?? [])
                              .map((r) => t(`role.${r}`))
                              .join(", ")
                          : t("users.noRoles")}
                      </dd>
                      <dt>{t("users.scope")}</dt>
                      <dd>
                        <Show
                          when={(u().roles ?? []).some((r) => r !== "member")}
                        >
                          <div>{t("users.wholeOrganisation")}</div>
                        </Show>
                        <For
                          each={props.grants.filter(
                            (g) => g.userId === u().id && g.state === "active",
                          )}
                        >
                          {(g) => (
                            <div>
                              {nodeName(g.nodeId)} ·{" "}
                              {t(`users.grantRole.${g.role}`)}
                              <Show when={g.validUntil}>
                                {(until) => (
                                  <span class="hint">
                                    {" "}
                                    ·{" "}
                                    {t("users.until", {
                                      date: formatDate(until(), locale()),
                                    })}
                                  </span>
                                )}
                              </Show>
                            </div>
                          )}
                        </For>
                      </dd>
                      <dt>{t("users.lastSeen")}</dt>
                      <dd>{formatDateTime(u().lastSeen, locale())}</dd>
                      <dt>{t("users.rules")}</dt>
                      <dd>{t("users.ruleCount", { count: ruleCount(u()) })}</dd>
                      <dt>{t("users.reports")}</dt>
                      <dd>
                        {reportsOf(u()).length > 0
                          ? reportsOf(u())
                              .map((r) => r.name)
                              .join(", ")
                          : "—"}
                      </dd>
                      <dt>{t("users.tasks")}</dt>
                      <dd>
                        {t("users.taskCount", { count: tasksOf(u()).length })}
                      </dd>
                    </dl>
                    <Show when={canGrant()}>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          "flex-wrap": "wrap",
                        }}
                      >
                        <button
                          type="button"
                          class="btn"
                          onClick={() => props.onGrant({ user: u() })}
                        >
                          <Icon name="plus" />
                          {t("users.grant")}
                        </button>
                        <button
                          type="button"
                          class="btn"
                          onClick={() =>
                            props.onGrant({ user: u(), temporary: true })
                          }
                        >
                          <Icon name="clock" />
                          {t("users.temporary")}
                        </button>
                      </div>
                    </Show>
                    <p class="hint" style={{ margin: 0 }}>
                      {t("users.manageHint")}
                    </p>
                  </div>
                </section>
              )}
            </Show>
          </div>
        )}
      </Show>
    </Show>
  );
}

function Roles(props: { grantRoles: GrantRole[] }) {
  return (
    <>
      <section class="card">
        <div class="card-h">
          <h2>{t("users.grantMatrix")}</h2>
          <span class="meta">{t("users.grantMatrixMeta")}</span>
        </div>
        <div class="scrollx">
          <table class="tbl">
            <thead>
              <tr>
                <th>{t("users.role")}</th>
                <For each={CAPABILITIES}>
                  {(c) => <th>{t(`users.capability.${c}`)}</th>}
                </For>
              </tr>
            </thead>
            <tbody>
              <For each={props.grantRoles}>
                {(r) => (
                  <tr>
                    <td>
                      <b style={{ "font-weight": 500 }}>
                        {t(`users.grantRole.${r.key}`)}
                      </b>
                      <div class="hint">
                        {t(`users.grantRoleHint.${r.key}`)}
                      </div>
                    </td>
                    <For each={CAPABILITIES}>
                      {(c) => (
                        <td>
                          <Level value={r.levels[c] ?? 0} />
                        </td>
                      )}
                    </For>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
        <div class="card-b hint">{t("users.grantMatrixHint")}</div>
      </section>
      <section class="card">
        <div class="card-h">
          <h2>{t("users.matrix")}</h2>
          <span class="meta">{t("users.matrixMeta")}</span>
        </div>
        <div class="scrollx">
          <table class="tbl">
            <thead>
              <tr>
                <th>{t("users.role")}</th>
                <For each={AREAS}>{(a) => <th>{t(`users.area.${a}`)}</th>}</For>
              </tr>
            </thead>
            <tbody>
              <For each={ROLES}>
                {(r) => (
                  <tr>
                    <td>
                      <b style={{ "font-weight": 500 }}>{t(`role.${r}`)}</b>
                      <div class="hint">{t(`users.roleHint.${r}`)}</div>
                    </td>
                    <For each={AREAS}>
                      {(a) => (
                        <td>
                          {MATRIX[r][a] === 2 ? (
                            <span class="chip ok">{t("users.full")}</span>
                          ) : MATRIX[r][a] === 1 ? (
                            <span class="chip acc">{t("users.partial")}</span>
                          ) : (
                            <span class="hint">—</span>
                          )}
                        </td>
                      )}
                    </For>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
        <div class="card-b hint">{t("users.matrixHint")}</div>
      </section>
    </>
  );
}

function Level(props: { value: number }) {
  return props.value === 2 ? (
    <span class="chip ok">{t("users.full")}</span>
  ) : props.value === 1 ? (
    <span class="chip acc">{t("users.partial")}</span>
  ) : (
    <span class="hint">—</span>
  );
}

/** Grants and invitations in the person's reach: who, which role where, until when, and revoking them. */
function Grants(props: {
  grants: AccessGrant[];
  loading: boolean;
  error: unknown;
  onChanged: () => void;
}) {
  const [revoking, setRevoking] = createSignal<AccessGrant>();
  const [users] = createResource(
    () => access().unrestricted || undefined,
    () => api.events.users(),
  );
  const who = (g: AccessGrant) =>
    g.userId
      ? ((users() ?? []).find((u) => u.id === g.userId)?.name ?? g.userId)
      : (g.email ?? "");
  return (
    <section class="card">
      <div class="card-h">
        <h2>{t("users.grants")}</h2>
        <span class="meta">{t("users.grantsMeta")}</span>
      </div>
      <Show when={!props.error} fallback={<ErrorBox error={props.error} />}>
        <Show
          when={props.grants.length > 0}
          fallback={
            <div class="card-b hint">
              {props.loading ? "…" : t("users.noGrants")}
            </div>
          }
        >
          <div class="scrollx">
            <table class="tbl">
              <thead>
                <tr>
                  <th>{t("users.who")}</th>
                  <th>{t("users.role")}</th>
                  <th>{t("users.where")}</th>
                  <th>{t("users.validUntil")}</th>
                  <th>{t("users.state")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <For each={props.grants}>
                  {(g) => (
                    <tr>
                      <td>
                        {who(g)}
                        <div class="hint">
                          {g.createdBy} ·{" "}
                          {formatDateTime(g.createdAt, locale())}
                        </div>
                      </td>
                      <td>{t(`users.grantRole.${g.role}`)}</td>
                      <td>{nodeName(g.nodeId)}</td>
                      <td>
                        {g.validUntil
                          ? formatDate(g.validUntil, locale())
                          : t("users.noEnd")}
                      </td>
                      <td>
                        <span class={`chip ${STATE_CHIP[g.state] ?? ""}`}>
                          {t(`users.grantState.${g.state}`)}
                        </span>
                      </td>
                      <td class="r">
                        <Show
                          when={
                            canGrant() &&
                            (g.state === "active" || g.state === "invited")
                          }
                        >
                          <button
                            type="button"
                            class="btn ghost sm"
                            onClick={() => setRevoking(g)}
                          >
                            <Icon name="x" />
                            {t("users.revoke")}
                          </button>
                        </Show>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </Show>
      </Show>
      <div class="card-b hint">{t("users.grantsHint")}</div>
      <Show when={revoking()}>
        {(g) => (
          <ConfirmDialog
            title={t("users.revokeTitle", { who: who(g()) })}
            text={t("users.revokeText", { where: nodeName(g().nodeId) })}
            confirm={t("users.revoke")}
            danger
            onConfirm={async () => {
              await api.access.revoke(g().id);
              props.onChanged();
            }}
            onClose={() => setRevoking(undefined)}
          />
        )}
      </Show>
    </section>
  );
}

/**
 * Gives a person a role on a node and everything below it, or invites an address that takes the grant over at its
 * first sign-in. A person with partial access to users invites observers to their own branches only.
 */
function GrantDialog(props: {
  target: GrantTarget;
  roles: GrantRole[];
  onClose: () => void;
  onSaved: (directory: string) => void;
}) {
  const partial = () =>
    !access().unrestricted && (access().capabilities["users"] ?? 0) < 2;
  const roles = () =>
    partial() ? props.roles.filter((r) => r.key === "observer") : props.roles;
  const nodes = () =>
    treeRows(siteTree()?.nodes ?? []).filter(
      (r) => access().unrestricted || access().nodes.includes(r.node.id),
    );
  const [email, setEmail] = createSignal("");
  const [role, setRole] = createSignal(partial() ? "observer" : "observer");
  const [node, setNode] = createSignal(nodes()[0]?.node.id ?? "");
  const [timed, setTimed] = createSignal(!!props.target.temporary);
  const [until, setUntil] = createSignal("");
  const [run, error, busy] = useAction();
  const valid = () =>
    !!node() &&
    !!role() &&
    (props.target.user || /.+@.+\..+/.test(email().trim())) &&
    (!timed() || !!until());
  return (
    <Dialog
      title={
        props.target.user
          ? t("users.grantTitle", { name: props.target.user.name })
          : t("users.inviteTitle")
      }
      onClose={props.onClose}
      footer={
        <>
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            class="btn primary"
            disabled={busy() || !valid()}
            onClick={() =>
              void run(async () => {
                const created = await api.access.grant({
                  userId: props.target.user?.id ?? null,
                  email: props.target.user ? null : email().trim(),
                  role: role(),
                  nodeId: node(),
                  validUntil:
                    timed() && until()
                      ? new Date(`${until()}T23:59:59`).toISOString()
                      : null,
                });
                props.onSaved(created.directory);
                props.onClose();
              })
            }
          >
            {props.target.user ? t("users.grantSave") : t("users.inviteSave")}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gap: "10px" }}>
        <Show when={!props.target.user}>
          <div class="field">
            <label for="grMail">{t("users.email")}</label>
            <input
              id="grMail"
              type="email"
              value={email()}
              placeholder="jan.kowalski@firma.pl"
              onInput={(e) => setEmail(e.currentTarget.value)}
            />
            <span class="hint">{t("users.inviteHint")}</span>
          </div>
        </Show>
        <div class="field">
          <label for="grRole">{t("users.role")}</label>
          <select
            id="grRole"
            value={role()}
            onChange={(e) => setRole(e.currentTarget.value)}
          >
            <For each={roles()}>
              {(r) => (
                <option value={r.key}>{t(`users.grantRole.${r.key}`)}</option>
              )}
            </For>
          </select>
          <span class="hint">{t(`users.grantRoleHint.${role()}`)}</span>
        </div>
        <div class="field">
          <label for="grNode">{t("users.where")}</label>
          <select
            id="grNode"
            value={node()}
            onChange={(e) => setNode(e.currentTarget.value)}
          >
            <For each={nodes()}>
              {(r) => (
                <option value={r.node.id}>
                  {"\u00a0\u00a0".repeat(r.depth)}
                  {r.node.name}
                </option>
              )}
            </For>
          </select>
          <span class="hint">{t("users.whereHint")}</span>
        </div>
        <label class="opt">
          <input
            type="checkbox"
            checked={timed()}
            onChange={(e) => setTimed(e.currentTarget.checked)}
          />
          <div>
            <b>{t("users.temporary")}</b>
            <small>{t("users.temporaryHint")}</small>
          </div>
        </label>
        <Show when={timed()}>
          <div class="field">
            <label for="grUntil">{t("users.validUntil")}</label>
            <input
              id="grUntil"
              type="date"
              value={until()}
              min={new Date().toISOString().slice(0, 10)}
              onInput={(e) => setUntil(e.currentTarget.value)}
            />
          </div>
        </Show>
        <div class="field" style={{ opacity: 0.55 }} title={t("users.soon")}>
          <label for="grHistory">{t("users.history")}</label>
          <select id="grHistory" disabled>
            <option>{t("users.historyAll")}</option>
          </select>
          <span class="hint">{t("users.historySoon")}</span>
        </div>
        <Show when={error()}>
          <ErrorBox error={error()} />
        </Show>
      </div>
    </Dialog>
  );
}

const HISTORY_CHIP: Record<string, string> = {
  granted: "ok",
  accepted: "acc",
  revoked: "crit",
  expired: "",
};

const localInput = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

/**
 * Who had access to what and when: every grant's history (given, accepted, revoked, expired, by whom), and the
 * answer to "who could see this place at that moment". Roles from the identity provider reach everything and are
 * not part of it.
 */
function History() {
  const [history] = createResource(() => api.access.history());
  const [users] = createResource(
    () => access().unrestricted || undefined,
    () => api.events.users(),
  );
  const who = (userId: string | null, email: string | null) =>
    userId
      ? ((users() ?? []).find((u) => u.id === userId)?.name ?? userId)
      : (email ?? "");
  const places = () => treeRows(siteTree()?.nodes ?? []);
  const [node, setNode] = createSignal(places()[0]?.node.id ?? "");
  const [at, setAt] = createSignal(localInput(new Date()));
  const [then] = createResource(
    () => (node() && at() ? { node: node(), at: new Date(at()) } : undefined),
    (q) => api.access.at(q.node, q.at),
  );
  return (
    <>
      <section class="card">
        <div class="card-h">
          <h2>{t("users.history.atTitle")}</h2>
        </div>
        <div class="card-b" style={{ display: "grid", gap: "10px" }}>
          <div class="grid g-2e" style={{ gap: "10px" }}>
            <div class="field">
              <label for="histNode">{t("users.where")}</label>
              <select
                id="histNode"
                value={node()}
                onChange={(e) => setNode(e.currentTarget.value)}
              >
                <For each={places()}>
                  {(row) => (
                    <option value={row.node.id}>
                      {"  ".repeat(row.depth)}
                      {row.node.name}
                    </option>
                  )}
                </For>
              </select>
            </div>
            <div class="field">
              <label for="histAt">{t("users.history.moment")}</label>
              <input
                id="histAt"
                type="datetime-local"
                value={at()}
                onInput={(e) => setAt(e.currentTarget.value)}
              />
            </div>
          </div>
          <Show when={then()} fallback={<Skeleton lines={2} />}>
            {(list) => (
              <Show
                when={list().length > 0}
                fallback={
                  <p class="hint" style={{ margin: 0 }}>
                    {t("users.history.nobody")}
                  </p>
                }
              >
                <table class="tbl">
                  <tbody>
                    <For each={list()}>
                      {(g) => (
                        <tr>
                          <td>{who(g.userId, g.email)}</td>
                          <td>{t(`users.grantRole.${g.role}`)}</td>
                          <td>{nodeName(g.nodeId)}</td>
                          <td class="hint">
                            {g.validUntil
                              ? t("users.history.until", {
                                  date: formatDate(g.validUntil, locale()),
                                })
                              : t("users.noEnd")}
                          </td>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
              </Show>
            )}
          </Show>
          <p class="hint" style={{ margin: 0 }}>
            {t("users.history.wideRoles")}
          </p>
        </div>
      </section>
      <section class="card">
        <div class="card-h">
          <h2>{t("users.history.title")}</h2>
          <span class="meta">{t("users.history.meta")}</span>
        </div>
        <Show
          when={!history.error}
          fallback={<ErrorBox error={history.error} />}
        >
          <Show when={history()} fallback={<Skeleton lines={5} />}>
            {(list) => (
              <Show
                when={list().length > 0}
                fallback={<div class="card-b hint">{t("users.noGrants")}</div>}
              >
                <div class="scrollx">
                  <table class="tbl">
                    <thead>
                      <tr>
                        <th>{t("users.history.when")}</th>
                        <th>{t("users.history.what")}</th>
                        <th>{t("users.who")}</th>
                        <th>{t("users.role")}</th>
                        <th>{t("users.where")}</th>
                        <th>{t("users.history.by")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={list()}>
                        {(e) => (
                          <tr>
                            <td
                              class="num hint"
                              style={{ "white-space": "nowrap" }}
                            >
                              {formatDateTime(e.at, locale())}
                            </td>
                            <td>
                              <span
                                class={`chip ${HISTORY_CHIP[e.kind] ?? ""}`}
                              >
                                {t(`users.history.kind.${e.kind}`)}
                              </span>
                            </td>
                            <td>{who(e.userId, e.email)}</td>
                            <td>{t(`users.grantRole.${e.role}`)}</td>
                            <td>{nodeName(e.nodeId)}</td>
                            <td class="hint">
                              {e.actor
                                ? who(e.actor, e.actor)
                                : e.kind === "expired"
                                  ? t("users.history.automatic")
                                  : "—"}
                              <Show when={!e.recorded && e.kind !== "expired"}>
                                {" "}
                                <span
                                  title={t("users.history.reconstructedHint")}
                                >
                                  *
                                </span>
                              </Show>
                            </td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </div>
              </Show>
            )}
          </Show>
        </Show>
        <div class="card-b hint">{t("users.history.hint")}</div>
      </section>
    </>
  );
}
